import { useState, useEffect, useMemo } from 'react'
import { debounce } from '@/utils/debounce'
import { DEBOUNCE_INPUT_TIME } from '@/const/config'
import FormPanel from "@/components/ui/Form/components/FormPanel/FormPanel.tsx";
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn.tsx";
import FormLabel from "@/components/ui/Form/components/FormLabel/FormLabel.tsx";
import FormTitle from "@/components/ui/Form/components/FormTitle/FormTitle.tsx";
import FormDivider from "@/components/ui/Form/components/FormDivider/FormDivider.tsx";
import Button from "../../../../ui/Button/Button.tsx";
import Input from "../../../../ui/Input/Input.tsx";
import PrefixInput from "@/components/ui/PrefixInput/PrefixInput.tsx";
import WorkspaceSettingsTitle from '../WorkspaceSettingsTitle/WorkspaceSettingsTitle.tsx'
import { workspaceNameError, WORKSPACE_NAME_MAX_LENGTH } from '@/utils/workspaceName'
import WorkspaceSettingsAvatar from '../WorkspaceSettingsAvatar/WorkspaceSettingsAvatar.tsx'
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks.ts";
import { openConfirmDialog } from "../../../../../store/ui/uiSlice.ts";
import { updateWorkspaceNameSaga, updateWorkspaceCompanySizeSaga, updateWorkspaceUrlSaga, updateWorkspaceIdentifierSaga, updateWorkspaceTimezoneSaga, deleteWorkspaceSaga } from "../../../../../store/workspace/workspaceSlice.ts";
import { TextNorm } from "../../../../ui/Text";
import IconTrash from "@/assets/icons/icon-trash.svg?react"
import Select from "../../../../ui/Select/Select.tsx";
import FormRow from "@/components/ui/Form/components/FormRow/FormRow.tsx";
import FormError from "@/components/ui/Form/components/FormError/FormError.tsx";

const getUtcOffset = (tz: string): string =>
    new Intl.DateTimeFormat('en', {timeZone: tz, timeZoneName: 'shortOffset'})
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value ?? ''

const getUtcOffsetMinutes = (tz: string): number => {
    const date = new Date()
    const utcMs = date.getTime() + date.getTimezoneOffset() * 60000
    const localMs = new Date(date.toLocaleString('en-US', {timeZone: tz})).getTime()
    return Math.round((localMs - utcMs) / 60000)
}

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone')
    .map(tz => ({value: tz, label: `${getUtcOffset(tz)} — ${tz}`, offset: getUtcOffsetMinutes(tz)}))
    .sort((a, b) => a.offset - b.offset || a.value.localeCompare(b.value))

const WorkspaceSettingsModalGeneral = () => {
    const dispatch = useAppDispatch()
    const workspace = useAppSelector(state => state.workspace.workspace)
    const [workspaceName, setWorkspaceName] = useState(workspace?.name ?? '')
    const [workspaceUrl, setWorkspaceUrl] = useState(workspace?.workspaceUrlId ?? '')
    const [identifier, setIdentifier] = useState(workspace?.workspaceTaskPrefixId ?? '')
    const [timezone, setTimezone] = useState(workspace?.timezone ?? '')
    const [nameError, setNameError] = useState<string | null>(null)
    const [urlError, setUrlError] = useState<string | null>(null)
    const [identifierError, setIdentifierError] = useState<string | null>(null)

    useEffect(() => { setWorkspaceName(workspace?.name ?? '') }, [workspace?.name])
    useEffect(() => { setWorkspaceUrl(workspace?.workspaceUrlId ?? '') }, [workspace?.workspaceUrlId])
    useEffect(() => { setIdentifier(workspace?.workspaceTaskPrefixId ?? '') }, [workspace?.workspaceTaskPrefixId])
    useEffect(() => { setTimezone(workspace?.timezone ?? '') }, [workspace?.timezone])

    const debouncedNameSave = useMemo(() => debounce((v: string) => dispatch(updateWorkspaceNameSaga(v)), DEBOUNCE_INPUT_TIME), [dispatch])
    const debouncedUrlSave = useMemo(() => debounce((v: string) => dispatch(updateWorkspaceUrlSaga({ value: v, onError: setUrlError })), DEBOUNCE_INPUT_TIME), [dispatch])
    const debouncedIdentifierSave = useMemo(() => debounce((v: string) => dispatch(updateWorkspaceIdentifierSaga({ value: v, onError: setIdentifierError })), DEBOUNCE_INPUT_TIME), [dispatch])

    useEffect(() => () => {
        debouncedNameSave.cancel()
        debouncedUrlSave.cancel()
        debouncedIdentifierSave.cancel()
    }, [debouncedNameSave, debouncedUrlSave, debouncedIdentifierSave])

    const handleWorkspaceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setWorkspaceName(value)
        // Shows the length/link rule inline instead of letting the write reach
        // firestore.rules and come back as a bare permission-denied (SCD-1534).
        const error = workspaceNameError(value)
        setNameError(error)
        if (!error) {
            debouncedNameSave(value.trim())
        } else {
            debouncedNameSave.cancel()
        }
    }

    const handleWorkspaceUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setWorkspaceUrl(value)
        setUrlError(null)
        debouncedUrlSave(value)
    }

    const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setIdentifier(value)
        setIdentifierError(null)
        debouncedIdentifierSave(value)
    }

    const handleTimezoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const tz = e.target.value
        setTimezone(tz)
        dispatch(updateWorkspaceTimezoneSaga(tz))
    }

    const handleCompanySizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(updateWorkspaceCompanySizeSaga(e.target.value))
    }

    const handleDeleteWorkspace = () => {
        dispatch(openConfirmDialog({
            title: 'Delete workspace',
            text: `Are you sure you want to delete this workspace? This cannot be undone.`,
            confirmButtonTitle: 'Delete',
            confirmButtonAction: () => { dispatch(deleteWorkspaceSaga()) }
        }))
    }

    return (
        <>
            <WorkspaceSettingsTitle>Workspace</WorkspaceSettingsTitle>
            <FormDivider />
            <FormColumn>
                <FormColumn gap={16}>
                    <FormTitle>General settings</FormTitle>
                    <WorkspaceSettingsAvatar />
                    <FormColumn>
                        <FormLabel>Name</FormLabel>
                        <Input
                            value={workspaceName}
                            onChange={handleWorkspaceNameChange}
                            error={!!nameError}
                            maxLength={WORKSPACE_NAME_MAX_LENGTH}
                        />
                        <FormError>{nameError}</FormError>
                    </FormColumn>
                    <FormColumn>
                        <FormRow>
                            <FormColumn>
                                <FormLabel>Workspace URL</FormLabel>
                                <PrefixInput
                                    prefix={`${import.meta.env.VITE_APP_DOMAIN}/`}
                                    value={workspaceUrl}
                                    onChange={handleWorkspaceUrlChange}
                                    error={!!urlError}
                                />
                            </FormColumn>
                            <FormColumn width={96}>
                                <FormLabel>Identifier</FormLabel>
                                <Input
                                    value={identifier}
                                    onChange={handleIdentifierChange}
                                    error={!!identifierError}
                                />
                            </FormColumn>
                        </FormRow>
                        <FormError>{urlError}</FormError>
                        <FormError>{identifierError}</FormError>
                    </FormColumn>
                    <FormColumn>
                        <FormLabel>Timezone {timezone}</FormLabel>
                        <Select
                            value={timezone}
                            onChange={handleTimezoneChange}
                            options={TIMEZONE_OPTIONS}
                            placeholder="Select timezone"
                        />
                    </FormColumn>
                    <FormColumn>
                        <FormLabel>How big is your company?</FormLabel>
                        <Select
                            value={workspace?.companySize ?? ''}
                            onChange={handleCompanySizeChange}
                            placeholder="Select size"
                            options={[
                                {value: '1-10', label: '1–10 employees'},
                                {value: '11-50', label: '11–50 employees'},
                                {value: '51-200', label: '51–200 employees'},
                                {value: '201-500', label: '201–500 employees'},
                                {value: '500+', label: '500+ employees'},
                            ]}
                        />
                    </FormColumn>
                </FormColumn>
                <FormDivider />
                <FormColumn>
                    <FormTitle>Danger zone</FormTitle>
                    <FormPanel>
                        <Button type={"danger_transparent"} title={"Delete workspace"} icon={<IconTrash />} onClick={handleDeleteWorkspace} />
                        <TextNorm>This will immediately and permanently delete this workspace and its data for everyone – including all projects and tasks. This cannot be undone.</TextNorm>
                    </FormPanel>
                </FormColumn>
            </FormColumn>
        </>
    )
}

export default WorkspaceSettingsModalGeneral
